<template>
  <detail-section title="Status" :editable="editable" :commit="commit">
    <template #default="{isEditing}">
      <div class="section-content">
        <LicenseStatusView v-if="!isEditing" :license="license" inverted class="align-self-start" />
        <select v-else v-model="status" class="form-select form-select-sm">
          <option v-for="option in license.statusOptions" :key="option.code" :value="option.code">{{ option.label }}</option>
        </select>
      </div>
      <div class="d-flex flex-row flex-wrap gap-3">
        <div class="section-detail">
          <div class="section-label">Applied</div>
          <div v-if="!isEditing">{{ license.dateAppliedFormatted ?? '---' }}</div>
          <date-input v-else v-model="appliedOn" small />
        </div>
        <div class="section-detail">
          <div class="section-label">Issued</div>
          <div v-if="!isEditing">{{ license.dateIssuedFormatted ?? '---' }}</div>
          <date-input v-else v-model="issuedOn" small />
        </div>
        <div class="section-detail">
          <div class="section-label">Expires</div>
          <div v-if="!isEditing">{{ license.expirationDateFormatted ?? '---' }}</div>
          <template v-else>
            <date-input :model-value="license.expiresOn" small disabled />
            <div class="text-muted small">Derived automatically</div>
          </template>
        </div>
      </div>
    </template>
  </detail-section>
  <LicenseStatusEmailConfirmationModal ref="statusEmailConfirmation" />
</template>

<script>
import {
  LEGACY_LICENSE_APPROVAL_EMAIL_RULE_ID,
  getLicenseStatusEmailRulesRequiringConfirmation
} from '@strenforcement/domain/licenseStatusEmail';
import DetailSection from "@/components/common/DetailSection.vue";
import {License} from "@/models/License";
import LicenseStatusView from "@/components/LicenseStatusView.vue";
import DateInput from "@/components/common/DateInput.vue";
import LicenseStatusEmailConfirmationModal from '@/components/licenses/LicenseStatusEmailConfirmationModal.vue';
import { showLicenseStatusEmailNotificationToasts } from '@/licenseStatusEmailNotification';
import { licenseDatesEqual } from '@strenforcement/domain/licenseDates';

export default {
  components: {
    LicenseStatusEmailConfirmationModal,
    DateInput,
    LicenseStatusView,
    DetailSection
  },
  props: {
    license: License,
    editable: Boolean
  },
  data() {
    return {
      status: null,
      appliedOn: null,
      issuedOn: null
    };
  },
  watch: {
    license: {
      immediate: true,
      handler() {
        this.resetFields();
      }
    }
  },
  methods: {
    resetFields() {
      this.status = this.license.status;
      this.appliedOn = this.license.appliedOn;
      this.issuedOn = this.license.issuedOn;
    },
    async commit(note) {
      let emailNotificationDecisions;
      const confirmationRules = getLicenseStatusEmailRulesRequiringConfirmation({
        rules: this.license.city.licenseStatusEmailRules,
        previousStatus: this.license.data.status,
        nextStatus: this.status,
        statusConfig: this.license.statusOptions
      });
      const recipientEmail = String(this.license.contact?.email || '').trim();
      if (confirmationRules.length && recipientEmail) {
        emailNotificationDecisions = await this.$refs.statusEmailConfirmation.guard({
          recipientEmail,
          rules: confirmationRules
        });
        if (!emailNotificationDecisions) {
          this.resetFields();
          return;
        }
      }

      const approvalNotificationDecision = emailNotificationDecisions?.[
        LEGACY_LICENSE_APPROVAL_EMAIL_RULE_ID
      ];

      const updatedLicense = await this.license.updateStatusAndDates({
        status: this.status,
        ...(!licenseDatesEqual(this.appliedOn, this.license.appliedOn, { dateOnly: true })
          ? { appliedOn: this.appliedOn } : {}),
        ...(!licenseDatesEqual(this.issuedOn, this.license.issuedOn, { dateOnly: true })
          ? { issuedOn: this.issuedOn } : {}),
        note,
        approvalNotificationDecision,
        emailNotificationDecisions
      });
      showLicenseStatusEmailNotificationToasts(
        this,
        updatedLicense.emailNotifications?.length
          ? updatedLicense.emailNotifications
          : (updatedLicense.approvalNotification ? [updatedLicense.approvalNotification] : [])
      );
    }
  }
}
</script>
